import * as React from 'react';

import { PaywallTouch } from '~teacher/components/PaywallDialog';

import { assertUnreachable } from '../../../../util/assertUnreachable';

type RoomsPaywallState = {
  isOpen: boolean;
  paywallTouch: PaywallTouch;
};

export const RoomsPaywallStateContext =
  React.createContext<RoomsPaywallState>(undefined);

export const RoomsPaywallDispatchContext =
  React.createContext<React.Dispatch<RoomsPaywallAction>>(undefined);

export type RoomsPaywallAction =
  | { type: 'close' }
  | { type: 'open'; payload: { paywallTouch: PaywallTouch } };

function roomsPaywallReducer(
  state: RoomsPaywallState,
  action: RoomsPaywallAction
) {
  switch (action.type) {
    case 'close':
      return {
        isOpen: false,
        paywallTouch: [null] as PaywallTouch,
      };
    case 'open': {
      const { paywallTouch } = action.payload;
      return {
        isOpen: true,
        paywallTouch: paywallTouch,
      };
    }
    default:
      // Ensure all actions are handled
      assertUnreachable(action);
  }
}

export function RoomsPaywallProvider({
  children,
  isOpen = false,
  paywallTouch = [null] as PaywallTouch,
}: {
  children: React.ReactNode;
  isOpen?: boolean;
  paywallTouch?: PaywallTouch;
}) {
  const [state, dispatch] = React.useReducer(roomsPaywallReducer, {
    isOpen: isOpen,
    paywallTouch: paywallTouch,
  });

  return (
    <RoomsPaywallStateContext.Provider value={state}>
      <RoomsPaywallDispatchContext.Provider value={dispatch}>
        {children}
      </RoomsPaywallDispatchContext.Provider>
    </RoomsPaywallStateContext.Provider>
  );
}

export function useRoomsPaywallState() {
  const context = React.useContext(RoomsPaywallStateContext);
  if (context === undefined) {
    throw new Error('RoomsPaywallStateContext is not defined.');
  }
  return context;
}

export function useRoomsPaywallDispatch() {
  const context = React.useContext(RoomsPaywallDispatchContext);
  if (context === undefined) {
    throw new Error('RoomsPaywallDispatchContext is not defined.');
  }
  return context;
}
